import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, inject, Input, ChangeDetectionStrategy } from '@angular/core';
import { AlertController, IonButton, IonIcon, IonicSafeString } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { ClickStopPropagationDirective } from '@okcargo-apps/ng-utils';
import { getEmptyFreightBasicForShipper, IFreightBasicForShipper } from '@okcargo-apps/shared-domain';
import { ConnectivityService } from '@okcargo-apps/shared-infrastructure';
import { FreightShipperService } from '../freight-shipper.service';

@Component({
  selector: 'lib-price-acceptance-buttons',
  templateUrl: './price-acceptance-buttons.component.html',
  styleUrls: ['./price-acceptance-buttons.component.scss'],
  imports: [IonIcon, IonButton, ClickStopPropagationDirective, CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DecimalPipe],
})
export class PriceAcceptanceButtonsComponent {
  private readonly freightShipService = inject(FreightShipperService);
  private readonly translate = inject(TranslateService);
  private readonly alertController = inject(AlertController);
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly connectivity = inject(ConnectivityService);

  /** Accepting/rejecting a price is a command: it cannot be done without connection. */
  protected readonly isOffline = this.connectivity.isOffline;

  @Input()
  freight: IFreightBasicForShipper = getEmptyFreightBasicForShipper();

  @Input()
  sizeSmall = true;

  public async showAlertAcceptPrice(): Promise<void> {
    const firstStopMsg = this.freight.stopsBasic[0].address.postalCode + ' ' + this.freight.stopsBasic[0].address.city;
    const lastStopMsg =
      this.freight.stopsBasic[this.freight.stopsBasic.length - 1].address.postalCode +
      ' ' +
      this.freight.stopsBasic[this.freight.stopsBasic.length - 1].address.city;

    const message = this.translate.instant('freight.post.accept_msg', {
      firstStop: firstStopMsg,
      lastStop: lastStopMsg,
      // TODO move to custom pipe for price
      price: this.decimalPipe.transform(this.freight.price, '1.2-2'),
    });

    const alert = await this.alertController.create({
      header: this.translate.instant('freight.post.accept_price'),
      message: new IonicSafeString(message),
      buttons: this._alertAcceptButtons,
    });

    await alert.present();
  }

  public async showAlertRejectPrice(): Promise<void> {
    const firstStopMsg = this.freight.stopsBasic[0].address.postalCode + ' ' + this.freight.stopsBasic[0].address.city;
    const lastStopMsg =
      this.freight.stopsBasic[this.freight.stopsBasic.length - 1].address.postalCode +
      ' ' +
      this.freight.stopsBasic[this.freight.stopsBasic.length - 1].address.city;

    const message = this.translate.instant('freight.post.reject_msg', {
      firstStop: firstStopMsg,
      lastStop: lastStopMsg,
      // TODO move to custom pipe for price
      price: this.decimalPipe.transform(this.freight.price, '1.2-2'),
    });

    const alert = await this.alertController.create({
      header: this.translate.instant('general.attention'),
      message: new IonicSafeString(message),
      buttons: this._alertRejectButtons,
    });

    await alert.present();
  }

  get size(): string {
    if (this.sizeSmall) {
      return 'small';
    }
    return 'default';
  }

  private readonly _alertAcceptButtons = [
    {
      text: this.translate.instant('general.cancel'),
      role: 'cancel',
      handler: () => {
        // console.log('Alert canceled');
      },
    },
    {
      text: this.translate.instant('freight.post.accept_price'),
      role: 'confirm',
      handler: this.handleAccept.bind(this),
    },
  ];

  private readonly _alertRejectButtons = [
    {
      text: this.translate.instant('general.cancel'),
      role: 'cancel',
      handler: () => {
        // console.log('Alert canceled');
      },
    },
    {
      text: 'Rechazo este precio',
      role: 'destructive',
      handler: this.handleReject.bind(this),
    },
  ];

  private async handleAccept(): Promise<void> {
    if (this.freight.id) {
      await this.freightShipService.acceptPrice(this.freight);
      // TODO review and remove timeout
      await new Promise((resolve) => setTimeout(resolve, 500));
      this.freightShipService.refreshFreightsShipFinished();
      this.freightShipService.refreshFreightsShipNotFinished();
    }
  }

  private async handleReject(): Promise<void> {
    if (this.freight.id) {
      await this.freightShipService.rejectPrice(this.freight);
      await new Promise((resolve) => setTimeout(resolve, 500));
      // TODO review and remove timeout
      this.freightShipService.refreshFreightsShipFinished();
      this.freightShipService.refreshFreightsShipNotFinished();
    }
  }
}
